import { ReportActionStatus, ReportInternalType, ReportStatus } from '@/__generated__/graphql';
import { AccessGuard } from '@/components/Analysis/shared/AccessGuard';
import * as state from '@/components/Analysis/state';
import { ConfirmationDialog } from '@/components/shared/ConfirmationDialog';
import { FilterAndSearchPanel } from '@/components/shared/FilterAndSearchPanel';
import { Snackbar } from '@/components/shared/Snackbar';
import { StructureCheck } from '@/components/shared/StructureCheck';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { abyssColors } from '@/theme/colors';
import { UpgradePromptBackground } from '@/types';
import { PictureAsPdf, Publish } from '@mui/icons-material';
import { Box, Button, CircularProgress, Stack, Tab, Tooltip } from '@mui/material';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { UpgradePrompt } from '../../shared/UpgradePrompt';
import { useFetchReportGroups } from '../hooks/useFetchReportGroups';
import { useFilteredReportsByType } from '../hooks/useFilteredReportsByType';
import { usePersistFilters } from '@/components/shared/FilterAndSearchPanel/usePersistFilters';
import { ReportGroups } from './ReportGroups';
import { ReportTypeTabs } from './ReportTypeTabs';
import { ReportsList } from './ReportsList';
import { FloatingLine, Panel, ReportTabs, ReportsMainStyles, containerStyles } from './styles';
import { useBulkReportDownload } from './useBulkReportDownload';
import { useBulkReportPublish } from './useBulkReportPublish';
import { useReportFilters } from './useReportFilters';
import { SelectedValues } from '../../../shared/FilterAndSearchPanel/FilterAndSearchPanel';
import { LocationTag } from '@/constants';
import { formatEnumForDisplay } from '@/utils';

const reportsTabs = [
  { key: 'ALL', label: 'All' },
  { key: ReportStatus.Draft, label: 'Drafts' },
  { key: ReportStatus.Published, label: 'Open' },
  { key: ReportStatus.Closed, label: 'Closed' },
];

const REPORT_FILTERS = [
  {
    id: 'actionStatus',
    name: 'Status',
    options: Object.values(ReportActionStatus).map((actionStatus) =>
      formatEnumForDisplay(actionStatus)
    ),
    key: 'actionStatus',
  },
];

const MAX_BULK_SELECTION = 5;

export const ReportsLanding = () => {
  const [selectedReportFilters, setSelectedReportFilters] = useAtom(state.selectedReportFilters);
  const [createdByMe, setCreatedByMe] = useAtom(state.createdByMeFilter);
  const [snackbarMessage, setSnackbarMessage] = useAtom(state.snackbarMessage);

  // Persist "Created by Me" filter state
  const { savedFilterKeys, persistFilterKeys } = usePersistFilters({
    filterKey: 'REPORTS-CREATED-BY-ME',
  });

  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    if (!hasInitialized && savedFilterKeys && savedFilterKeys.length > 0) {
      const persistedValue = savedFilterKeys[0] === 'true';
      setCreatedByMe(persistedValue);
      setHasInitialized(true);
    }
  }, [hasInitialized, savedFilterKeys, setCreatedByMe]);

  const handleCreatedByMeChange = useCallback((value: boolean) => {
    setCreatedByMe(value);
    persistFilterKeys([value ? 'true' : 'false']);
  }, [persistFilterKeys, setCreatedByMe]);

  const [downloadLoading, setDownloadLoading] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState<ReportStatus | 'ALL'>('ALL');
  const [searchTerm, setSearchTerm] = useState<string | undefined>();
  const [selectedReportIds, setSelectedReportIds] = useState<string[]>([]);
  const [selectedReportType, setSelectedReportType] = useState<ReportInternalType>(
    ReportInternalType.PointOfInterest
  );
  const [isPublishConfirmOpen, setIsPublishConfirmOpen] = useState(false);

  const structureId = useGetStructureId();
  const navbarHeight = useNavbarHeight();
  const { filters: componentFilters } = useReportFilters();
  const { reportGroups } = useFetchReportGroups(selectedReportType, currentTab, searchTerm);
  const { reports: reportsInView } = useFilteredReportsByType(
    selectedReportType,
    currentTab,
    searchTerm
  );
  const { handleBulkDownload } = useBulkReportDownload({
    selectedReportIds,
    setLoading: setDownloadLoading,
  });
  const { handleBulkPublish } = useBulkReportPublish({ setLoading: setPublishLoading });

  const draftReportIds = useMemo(
    () =>
      reportsInView
        .filter((r) => r?.status === ReportStatus.Draft)
        .map((r) => r?.id)
        .filter(Boolean) as string[],
    [reportsInView]
  );
  const draftIdsToPublish = useMemo(
    () => selectedReportIds.filter((id) => draftReportIds.includes(id)),
    [selectedReportIds, draftReportIds]
  );
  const hasDraftSelected = draftIdsToPublish.length > 0;
  const hasSelection = selectedReportIds.length > 0;
  const isOverLimit = selectedReportIds.length > MAX_BULK_SELECTION;

  const { shouldShow, content, persistent, duration } = snackbarMessage;

  const selectedReportTypeIsComponent = useMemo(() => {
    return selectedReportType === ReportInternalType.Component;
  }, [selectedReportType]);

  const closeSnack = (_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarMessage({ shouldShow: false, content: undefined });
  };

  const handleReportTypeChange = useCallback(
    (_: React.SyntheticEvent, newValue: ReportInternalType) => {
      setSelectedReportType(newValue);
      setSelectedReportIds([]);
      setIsPublishConfirmOpen(false);
    },
    [setSelectedReportType]
  );

  const handleTabActiveChange = (event: React.SyntheticEvent, newValue: ReportStatus) => {
    setCurrentTab(newValue);
    setSelectedReportIds([]);
    setIsPublishConfirmOpen(false);
  };

  const handleSelectedFilterValuesChange = useCallback(
    (selectedValues: SelectedValues) => {
      const filteredSelectedValues = Object.fromEntries(
        Object.entries(selectedValues).filter(([, values]) => values.length > 0)
      );
      setSelectedReportFilters(filteredSelectedValues);
    },
    [setSelectedReportFilters]
  );

  const filters = useMemo(
    () =>
      selectedReportTypeIsComponent ? [...componentFilters, ...REPORT_FILTERS] : REPORT_FILTERS,
    [selectedReportTypeIsComponent, componentFilters]
  );

  const toggleFilters = useMemo(
    () => [
      {
        label: 'Created by Me',
        checked: createdByMe,
        onChange: handleCreatedByMeChange,
      },
    ],
    [createdByMe, handleCreatedByMeChange]
  );
  return (
    <StructureCheck structureId={structureId}>
      <AccessGuard structureId={structureId} feature="reports">
        <UpgradePrompt
          feature="reports"
          featureTitle="Reports"
          background={UpgradePromptBackground.Reports}
          paragraphs={[
            'Access enhanced reports with automated site images',
            'View marked location of anomalies',
            'Export reports for streamlined tracking, seamless monitoring, and documentation purposes',
            'Add selected images of anomalies for greater clarity',
          ]}
        >
          <Stack
            direction="row"
            justifyContent="center"
            sx={containerStyles}
            height={`calc(100vh - ${navbarHeight}px)`}
          >
            <Box sx={ReportsMainStyles}>
              <ReportTabs value={currentTab} onChange={handleTabActiveChange}>
                {reportsTabs.map((tab) => (
                  <Tab
                    value={tab.key}
                    key={tab.key}
                    label={tab.label}
                    sx={{ textTransform: 'none' }}
                  />
                ))}
              </ReportTabs>
              <FloatingLine />
              <ReportTypeTabs
                selectedTab={selectedReportType}
                handleChange={handleReportTypeChange}
              />
              <Panel>
                <FilterAndSearchPanel
                  key={`filter-panel-${selectedReportType}`}
                  templateFields={filters}
                  selectedValues={selectedReportFilters}
                  setSelectedValues={setSelectedReportFilters}
                  handleSelectedChange={handleSelectedFilterValuesChange}
                  searchBar
                  searchPlaceholder="Search report"
                  searchTerm={searchTerm}
                  setSearch={setSearchTerm}
                  filterOptions
                  hideStaticFilters
                  location={LocationTag.Reports}
                  toggleFilters={toggleFilters}
                  endAdornment={
                    hasSelection ? (
                      <Stack direction="row" gap={1} alignItems="center">
                        <Tooltip
                          title={
                            isOverLimit
                              ? `Max selection is ${MAX_BULK_SELECTION} reports.`
                              : ''
                          }
                        >
                          <span>
                            <Button
                              variant="contained"
                              size="small"
                              sx={{
                                minWidth: 'auto',
                                whiteSpace: 'nowrap',
                                height: '3.2rem',
                              }}
                              startIcon={
                                downloadLoading ? (
                                  <CircularProgress
                                    size={20}
                                    sx={{ color: abyssColors.primary[500] }}
                                  />
                                ) : (
                                  <PictureAsPdf sx={{ cursor: 'pointer' }} />
                                )
                              }
                              onClick={handleBulkDownload}
                              disabled={downloadLoading || isOverLimit}
                            >
                              Download Selected
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            isOverLimit
                              ? `Max selection is ${MAX_BULK_SELECTION} reports.`
                              : !hasDraftSelected
                                ? 'Select at least one Draft report to publish.'
                                : ''
                          }
                        >
                          <span>
                            <Button
                              variant="contained"
                              size="small"
                              sx={{
                                minWidth: 'auto',
                                whiteSpace: 'nowrap',
                                height: '3.2rem',
                              }}
                              startIcon={
                                publishLoading ? (
                                  <CircularProgress
                                    size={20}
                                    sx={{ color: abyssColors.primary[500] }}
                                  />
                                ) : (
                                  <Publish sx={{ cursor: 'pointer' }} />
                                )
                              }
                              onClick={() => setIsPublishConfirmOpen(true)}
                              disabled={publishLoading || !hasDraftSelected || isOverLimit}
                            >
                              Publish Selected
                            </Button>
                          </span>
                        </Tooltip>
                      </Stack>
                    ) : null
                  }
                />
              </Panel>
              {reportGroups.length === 0 ? (
                <ReportsList
                  selectedReportType={selectedReportType}
                  status={currentTab}
                  searchTerm={searchTerm}
                  selectedReportIds={selectedReportIds}
                  setSelectedReportIds={setSelectedReportIds}
                />
              ) : (
                <>
                  {reportGroups.map((group) => (
                    <ReportGroups
                      key={group.groupId}
                      reportGroup={group}
                      selectedReportType={selectedReportType}
                      status={currentTab}
                      searchTerm={searchTerm}
                      selectedReportIds={selectedReportIds}
                      setSelectedReportIds={setSelectedReportIds}
                    />
                  ))}
                </>
              )}
              <Snackbar
                open={shouldShow}
                onClose={closeSnack}
                persistent={persistent}
                autoHideDuration={duration}
              >
                {content}
              </Snackbar>
              <ConfirmationDialog
                title="Publish selected reports?"
                description={`You are about to publish ${draftIdsToPublish.length} report${draftIdsToPublish.length === 1 ? '' : 's'}. Once published, you will not be able to edit ${draftIdsToPublish.length === 1 ? 'it' : 'them'}.`}
                confirmButtonText="Publish selected"
                cancelButtonText="Cancel"
                isOpen={isPublishConfirmOpen}
                handleClose={() => setIsPublishConfirmOpen(false)}
                onConfirm={() => handleBulkPublish(draftIdsToPublish)}
                loading={{
                  state: publishLoading,
                  text: 'Publishing...',
                }}
              />
            </Box>
          </Stack>
        </UpgradePrompt>
      </AccessGuard>
    </StructureCheck>
  );
};
